/**
 * What Moved the Needle: explains score changes between latest and previous snapshot
 * with top positive and negative contributors. Entity-level contributors show
 * clickable links to document/vehicle/authority pages when entityId is present.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody } from '../ui';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { formatAnalyticsDocumentDisplay } from '../../utils/analyticsDocumentLabel';

function getEntityRoute(entityType, entityId) {
  if (entityType === 'document') return '/documents';
  if (entityType === 'vehicle' && entityId) return `/vehicles/${entityId}`;
  if (entityType === 'authority') return '/authority';
  return null;
}

const SCORE_LABELS = {
  auditReadinessScore: 'Audit readiness',
  complianceScore: 'Compliance score',
  documentCompletenessScore: 'Document completeness',
  vehicleReadinessScore: 'Vehicle readiness',
  authorityHealthScore: 'Authority health',
  expirationRiskScore: 'Expiration (fewer expiring = better)',
};

function getSummaryLine(delta, direction, scoreLabel, higherIsBetter) {
  const absDelta = Math.abs(delta);
  if (direction === 'flat') return null;
  const improved = direction === 'improved';
  if (improved) {
    return `Your ${scoreLabel.toLowerCase()} improved by ${absDelta} point${absDelta === 1 ? '' : 's'}.`;
  }
  return `Your ${scoreLabel.toLowerCase()} dipped by ${absDelta} point${absDelta === 1 ? '' : 's'}.`;
}

export default function WhatMovedTheNeedle({ comparison, loading, comparisonWindowDays }) {
  const navigate = useNavigate();
  const subtitle = comparisonWindowDays
    ? `Why your scores changed (vs. ${comparisonWindowDays} days ago)`
    : 'Why your scores changed since the last snapshot';
  if (loading) {
    return (
      <Card className="analytics-card what-moved-the-needle">
        <CardHeader title="What moved the needle" subtitle={subtitle} />
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Loading...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!comparison?.deltas?.length) {
    return (
      <Card className="analytics-card what-moved-the-needle">
        <CardHeader title="What moved the needle" subtitle={subtitle} />
        <CardBody>
          <div className="analytics-building-history-card" role="status">
            <p className="analytics-building-history-card__eyebrow">BUILDING YOUR HISTORY</p>
            <p className="analytics-building-history-card__body">
              Score contributors will appear here after your next compliance snapshot — usually within 24 hours. The
              longer you use Sovrynn, the more detailed your intelligence becomes.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const improved = comparison.deltas.filter((d) => d.direction === 'improved' && d.contributors?.length);
  const declined = comparison.deltas.filter((d) => d.direction === 'declined' && d.contributors?.length);
  const hasChanges = improved.length > 0 || declined.length > 0;

  return (
    <Card className="analytics-card what-moved-the-needle">
      <CardHeader title="What moved the needle" subtitle={subtitle} />
      <CardBody>
        {!hasChanges && (
          <p className="what-needle-intro">
            No score changes since the last snapshot. You're holding steady.
          </p>
        )}
        {improved.length > 0 && (
          <div className="what-needle-group what-needle-improved">
            <h4 className="what-needle-group-title">
              <TrendingUp size={18} />
              Improvements
            </h4>
            <ul className="what-needle-list">
              {improved.map((d) => {
                const label = SCORE_LABELS[d.scoreName] || formatAnalyticsDocumentDisplay(d.scoreName) || d.scoreName;
                const summary = getSummaryLine(d.delta, d.direction, label, d.higherIsBetter);
                return (
                  <li key={d.scoreName} className="what-needle-item">
                    {summary && <p className="what-needle-summary">{summary}</p>}
                    {d.contributors?.map((c, i) => (
                      <p key={i} className="what-needle-contributor">
                        {formatAnalyticsDocumentDisplay(c.explanation)}
                        {c.entityId && getEntityRoute(c.entityType, c.entityId) && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="what-needle-entity-link"
                              onClick={() => navigate(getEntityRoute(c.entityType, c.entityId))}
                            >
                              {c.entityName
                                ? `View ${formatAnalyticsDocumentDisplay(c.entityName)}`
                                : c.entityType === 'document'
                                  ? 'View documents'
                                  : c.entityType === 'vehicle'
                                    ? 'View vehicle'
                                    : 'View'}
                            </button>
                          </>
                        )}
                      </p>
                    ))}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {declined.length > 0 && (
          <div className="what-needle-group what-needle-declined">
            <h4 className="what-needle-group-title">
              <TrendingDown size={18} />
              Areas to watch
            </h4>
            <ul className="what-needle-list">
              {declined.map((d) => {
                const label = SCORE_LABELS[d.scoreName] || formatAnalyticsDocumentDisplay(d.scoreName) || d.scoreName;
                const summary = getSummaryLine(d.delta, d.direction, label, d.higherIsBetter);
                return (
                  <li key={d.scoreName} className="what-needle-item">
                    {summary && <p className="what-needle-summary">{summary}</p>}
                    {d.contributors?.map((c, i) => (
                      <p key={i} className="what-needle-contributor">
                        {formatAnalyticsDocumentDisplay(c.explanation)}
                        {c.entityId && getEntityRoute(c.entityType, c.entityId) && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="what-needle-entity-link"
                              onClick={() => navigate(getEntityRoute(c.entityType, c.entityId))}
                            >
                              {c.entityName
                                ? `View ${formatAnalyticsDocumentDisplay(c.entityName)}`
                                : c.entityType === 'document'
                                  ? 'View documents'
                                  : c.entityType === 'vehicle'
                                    ? 'View vehicle'
                                    : 'View'}
                            </button>
                          </>
                        )}
                      </p>
                    ))}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
